import * as React from "react";
import { Link, withRouter } from "react-router-dom";

import { Loading } from "carbon-components-react";
import { connect } from "react-redux";

import { isProduction } from "config";
import APIClient from "web_modules/APIClient";
import { operatorIdFromToken, getSessionToken, tokenHasRoles } from "web_modules/shared/session";
import PipelineViewer, { ColumnDef, Data } from "web_modules/PipelineViewer";
import { TRUSOperators } from "web_modules/redux/getTrusOperators/types";

import { Role } from "@eave/shared/types/api";
import { convertToSlug, getSchema } from "./helpers";
import { filterViewFields, View, ViewsContext, ViewsProvider } from "./viewsContext";
import { PipelineDataProvider, PipelineFieldsContext } from "./pipelineFieldsContext";

const styles = require("./styles.module.scss");

export const PIPELINE_PAGE_URL = "/reporting/pipeline";
// https://github.com/homelight/eave-go/blob/69db698841958a98302cf4d62c3c5409ba58f4b6/db/tasking.go#L22
const PIPELINE_ID_FIELD = "loan_application_id";
const PIPELINE_QUERY_LIMIT = 10000;

const guardAgainstLimitedData = (data: Data) => {
  if (!data.length) {
    return;
  }
  const highestId = data[0][PIPELINE_ID_FIELD]; // since sorted desc
  if (highestId > PIPELINE_QUERY_LIMIT) {
    alert(
      `Data exceeds ${PIPELINE_QUERY_LIMIT} rows and could not be fully fetched.
Please contact engineering to assess.`,
    );
  }
};

// N.b asserts and assumes that the data we receive should explicitly set any nullable fields,
// while this sends more data down the wire it simplifies the FE logic of having to
// infer null/undefined values in the pipeline
export const errorForMissingSchema = (data: Data, fields: string[]): string => {
  const missingFieldSet = new Set();

  data.forEach(row => {
    fields.forEach(field => {
      if (row[field] === undefined) {
        missingFieldSet.add(field);
      }
    });
  });

  if (missingFieldSet.size !== 0) {
    const missingFields = Array.from(missingFieldSet.keys());
    return `Missing expected data field(s): ${missingFields.join(",")}`;
  }

  return "";
};

const checkEveryFieldHasSchemaDef = (data: Data, fields: string[]) => {
  const errMsg = errorForMissingSchema(data, fields);
  if (errMsg !== "") {
    const log = isProduction() ? console.warn : alert;
    log(errMsg);
  }
};

interface Props {
  viewId?: string;
  viewTitle?: string;

  // withRouter
  history: any;
  location: any;
  match: any;
}

interface State {
  data: Data;
  lastLoaded: number;
  loadedViews: View[] | null;
  operatorId: number;
  userCanEditLoanApp: boolean;
  schema: ColumnDef[];
}

interface PipelineFieldsResponse {
  pipeline_fields: Data;
  next: number;
}
const getPipelineFields = (lessThanID = 0, pipelineFields = []): Promise<Data> => {
  return APIClient.get(`/ro/pipeline_fields?less_than_id=${lessThanID}`).then(
    (data: PipelineFieldsResponse) => {
      const pFields = pipelineFields.concat(data.pipeline_fields);
      if (data.next !== null) {
        return getPipelineFields(data.next, pFields);
      }
      return pFields;
    },
  );
};

interface VisibleFieldsResponse {
  visible_fields: string[];
}
const getVisibleFields = (): Promise<string[]> => {
  return APIClient.get("/ro/visible_fields").then(
    (data: VisibleFieldsResponse) => data.visible_fields,
  );
};

interface PipelineViewsResponse {
  pipeline_views: View[];
}
const getPipelineViews = (): Promise<View[]> => {
  return APIClient.get("/ro/pipeline_views").then(
    (data: PipelineViewsResponse) => data.pipeline_views,
  );
};

const getOperators = (): Promise<TRUSOperators> => {
  return APIClient.get(`/uw/trus_operators`).then((operators: TRUSOperators) => operators);
};

class PipelinePage extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const sessionToken = getSessionToken();
    const operatorId = operatorIdFromToken(sessionToken);
    const userCanEditLoanApp = tokenHasRoles(sessionToken, Role.Admin, Role.Underwriter);

    this.state = {
      operatorId,
      userCanEditLoanApp,
      loadedViews: null,
      data: null,
      lastLoaded: null,
      schema: [],
    };
  }

  componentDidMount() {
    const { userCanEditLoanApp } = this.state;
    const lastLoaded = Date.now();

    const promises: Promise<any>[] = [getVisibleFields(), getPipelineViews(), getPipelineFields()];

    if (userCanEditLoanApp) {
      promises.push(getOperators());
    }

    Promise.all(promises)
      .then(data => {
        const [visible_fields, pipeline_views, pipeline_fields, operators] = data;
        const schema = getSchema(visible_fields, operators);
        const schemaFields = schema.map(s => s.jsonKey);
        const loadedViews = pipeline_views.map(view => filterViewFields(view, schemaFields));
        checkEveryFieldHasSchemaDef(pipeline_fields, schemaFields);

        this.setState({
          data: pipeline_fields,
          loadedViews,
          schema,
          lastLoaded,
        });
      })
      .catch(e => {
        window.alert("There was a problem initializing this page. Please contact eng.");
        throw e;
      });
  }

  componentDidUpdate() {
    const { viewId, history } = this.props;

    // N.b no viewId, means we're routed to homepage
    if (viewId === undefined) {
      return;
    }

    const invalidId = isNaN(parseInt(viewId, 10));

    if (invalidId) {
      history.push(PIPELINE_PAGE_URL);
    }
  }

  updatePinnedMessage = (appID: string, pinnedMsg) => {
    const { data } = this.state;
    const appIDInt = parseInt(appID, 10);

    const indexToUpdate = data.findIndex(r => r.loan_application_id === appIDInt);
    if (indexToUpdate === -1) return;

    data[indexToUpdate].pinned_message = pinnedMsg;
    this.setState({
      data: [...data],
    });
  };

  render() {
    const { viewId } = this.props;
    const { data, lastLoaded, loadedViews, operatorId, userCanEditLoanApp, schema } = this.state;

    if (!data || !loadedViews) {
      return (
        <div className={styles.container}>
          <h3>Pipeline</h3>
          <Loading />
        </div>
      );
    }

    return (
      <PipelineDataProvider
        loadedPipelineData={data}
        updatePinnedMessage={this.updatePinnedMessage}
      >
        <PipelineFieldsContext.Consumer>
          {({ pipelineData }) => (
            <ViewsProvider views={loadedViews}>
              <ViewsContext.Consumer>
                {({ views }) => {
                  const view = views.find(v => v.id === parseInt(viewId, 10));
                  if (view) {
                    return (
                      <div className={styles.container}>
                        <PipelineViewer
                          title={view.title}
                          schema={schema}
                          data={pipelineData}
                          lastLoaded={lastLoaded}
                          initialColumns={view.ordered_columns}
                          initialActions={view.serialized_actions}
                          linkTo={PIPELINE_PAGE_URL}
                          linkText="&larr; Back to views"
                          userCanEditLoanApp={userCanEditLoanApp}
                        />
                      </div>
                    );
                  }

                  return (
                    <div className={styles.container}>
                      <h3>Pipeline &ndash; Views</h3>
                      {views && views.length === 0 ? (
                        <>
                          <h5>No saved views</h5>
                        </>
                      ) : (
                        <>
                          <h5>Your saved views</h5>
                          <ul>
                            {views
                              .filter(v => v.operator_id === operatorId)
                              .map(v => (
                                <li key={v.id}>
                                  <Link
                                    to={`${PIPELINE_PAGE_URL}/${v.id}/${convertToSlug(v.title)}`}
                                  >
                                    {v.title}
                                  </Link>
                                </li>
                              ))}
                          </ul>
                          <br />
                          <h5>Others&#39; views</h5>
                          <ul>
                            {views
                              .filter(v => v.operator_id !== operatorId)
                              .map(v => (
                                <li key={v.id}>
                                  <span>
                                    <Link
                                      to={`${PIPELINE_PAGE_URL}/${v.id}/${convertToSlug(v.title)}`}
                                    >
                                      {v.title}
                                    </Link>
                                    <span className={styles.createdBy}>
                                      &nbsp; - created by {v.operator_name}
                                    </span>
                                  </span>
                                </li>
                              ))}
                          </ul>
                        </>
                      )}
                    </div>
                  );
                }}
              </ViewsContext.Consumer>
            </ViewsProvider>
          )}
        </PipelineFieldsContext.Consumer>
      </PipelineDataProvider>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  viewId: ownProps.match.params.viewId,
  viewTitle: ownProps.match.params.viewTitle,
});

const ConnectedPipelinePage = connect(mapStateToProps)(PipelinePage);

export default withRouter(ConnectedPipelinePage);
